import { useState } from 'react'
import type { ReactNode } from 'react'
import { Input } from '@/components/ui/Input'
import {
  selectClasses, textareaClasses, labelClasses, submitButtonClasses, capitalize, fieldError,
} from './formStyles'
import { POS_OPTIONS, GENDER_OPTIONS } from '@/types/customFlashcard'

export interface CustomCardValues {
  lemma: string
  translation: string
  part_of_speech: string
  gender: string
  example_sentence: string
  notes: string
}

export const EMPTY_CARD_VALUES: CustomCardValues = {
  lemma: '', translation: '', part_of_speech: '', gender: '', example_sentence: '', notes: '',
}

interface CustomCardFormProps {
  initial: CustomCardValues
  submitLabel: string
  submitting: boolean
  errors: Record<string, string[]>
  onSubmit: (values: CustomCardValues) => void
  footer?: ReactNode
}

/** Shared create/edit form for a custom flashcard. The parent owns the API call. */
export function CustomCardForm({ initial, submitLabel, submitting, errors, onSubmit, footer }: CustomCardFormProps) {
  const [values, setValues] = useState<CustomCardValues>(initial)

  function set<K extends keyof CustomCardValues>(key: K, value: string) {
    setValues((v) => ({ ...v, [key]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit(values)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Input
        id="custom-card-lemma"
        label="Word"
        value={values.lemma}
        onChange={(e) => set('lemma', e.target.value)}
        error={fieldError(errors, 'lemma')}
        required
      />
      <Input
        id="custom-card-translation"
        label="Translation"
        value={values.translation}
        onChange={(e) => set('translation', e.target.value)}
        error={fieldError(errors, 'translation')}
        required
      />

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="custom-card-pos" className={labelClasses}>Part of speech</label>
          <select
            id="custom-card-pos"
            value={values.part_of_speech}
            onChange={(e) => set('part_of_speech', e.target.value)}
            className={selectClasses}
          >
            <option value="">—</option>
            {POS_OPTIONS.map((pos) => (
              <option key={pos} value={pos}>{capitalize(pos)}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="custom-card-gender" className={labelClasses}>Gender</label>
          <select
            id="custom-card-gender"
            value={values.gender}
            onChange={(e) => set('gender', e.target.value)}
            className={selectClasses}
          >
            <option value="">—</option>
            {GENDER_OPTIONS.map((g) => (
              <option key={g} value={g}>{capitalize(g)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="custom-card-example" className={labelClasses}>Example sentence</label>
        <textarea
          id="custom-card-example"
          value={values.example_sentence}
          onChange={(e) => set('example_sentence', e.target.value)}
          rows={2}
          className={textareaClasses}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="custom-card-notes" className={labelClasses}>Notes</label>
        <textarea
          id="custom-card-notes"
          value={values.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={2}
          className={textareaClasses}
        />
      </div>

      {errors.base && (
        <p role="alert" className="text-xs text-error font-light">{errors.base[0]}</p>
      )}

      <div className="flex items-center justify-between gap-3 mt-1">
        {footer}
        <button type="submit" disabled={submitting} className={submitButtonClasses}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  )
}
